import React, { useState, useRef, useCallback } from 'react';
import { X, Plus } from 'lucide-react';

/**
 * Reusable Tags Input component.
 * Supports: dropdown with existing suggestions + "+ Add" for new tags,
 * Enter to add, Backspace to remove last, click X to remove, duplicate prevention.
 *
 * @param {Object} props
 * @param {string[]} props.tags - Current tags array
 * @param {Function} props.onChange - Called with updated tags array
 * @param {string[]} [props.suggestions] - Optional autocomplete suggestions
 * @param {string} [props.placeholder] - Input placeholder text
 * @param {boolean} [props.disabled] - Disable input
 * @param {boolean} [props.saving] - Show saving indicator
 * @param {string} [props.size] - 'sm' | 'md' (default 'sm')
 */
const TagsInput = ({
    tags = [],
    onChange,
    suggestions = [],
    placeholder = 'Add tag...',
    disabled = false,
    saving = false,
    size = 'sm',
}) => {
    const [inputValue, setInputValue] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef(null);

    const normalizeTag = (tag) => tag.trim();

    const isDuplicate = useCallback((tag) => {
        const lower = normalizeTag(tag).toLowerCase();
        return tags.some(t => t.toLowerCase() === lower);
    }, [tags]);

    const addTag = useCallback((raw) => {
        const tag = normalizeTag(raw);
        if (!tag || isDuplicate(tag)) return;
        onChange([...tags, tag]);
        setInputValue('');
        setShowDropdown(false);
    }, [tags, onChange, isDuplicate]);

    const removeTag = useCallback((index) => {
        onChange(tags.filter((_, i) => i !== index));
    }, [tags, onChange]);

    // Filter suggestions: not already selected, matches input
    const filteredSuggestions = suggestions.filter(s => {
        const lower = s.toLowerCase();
        return (
            !tags.some(t => t.toLowerCase() === lower) &&
            (inputValue.trim() === '' || lower.includes(inputValue.trim().toLowerCase()))
        );
    });

    // Determine if we should show the "+ Add" option
    const trimmedInput = inputValue.trim();
    const isNewTag = trimmedInput.length > 0 &&
        !isDuplicate(trimmedInput) &&
        !suggestions.some(s => s.toLowerCase() === trimmedInput.toLowerCase());

    // Show dropdown when there are filtered suggestions or a new tag can be created
    const hasDropdownContent = filteredSuggestions.length > 0 || isNewTag;

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (trimmedInput) {
                addTag(inputValue);
            }
        } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
            removeTag(tags.length - 1);
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
            inputRef.current?.blur();
        }
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        setShowDropdown(true);
    };

    const chipSize = size === 'md'
        ? 'px-2.5 py-1 text-xs'
        : 'px-2 py-0.5 text-[10px]';

    const inputSize = size === 'md'
        ? 'text-xs py-1.5'
        : 'text-[11px] py-1';

    return (
        <div className="relative">
            <div
                className="flex flex-wrap items-center gap-1.5 min-h-[32px] bg-white border border-notion-border rounded-lg px-2 py-1 focus-within:border-stone-400 transition cursor-text"
                onClick={() => inputRef.current?.focus()}
            >
                {tags.map((tag, index) => (
                    <span
                        key={`${tag}-${index}`}
                        className={`inline-flex items-center gap-1 bg-stone-100 text-stone-600 rounded border border-stone-200 ${chipSize}`}
                    >
                        {tag}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeTag(index);
                                }}
                                className="text-stone-400 hover:text-stone-700 transition"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => {
                        // Delay hiding so click on dropdown option registers
                        setTimeout(() => setShowDropdown(false), 150);
                    }}
                    placeholder={tags.length === 0 ? placeholder : ''}
                    disabled={disabled}
                    className={`flex-1 min-w-[80px] outline-none bg-transparent placeholder-stone-400 ${inputSize} disabled:cursor-not-allowed`}
                />
                {saving && (
                    <span className="text-[10px] text-stone-400 animate-pulse">Saving...</span>
                )}
            </div>

            {/* Dropdown: existing suggestions + "+ Add" for new tags */}
            {showDropdown && hasDropdownContent && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-notion-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {filteredSuggestions.map((suggestion) => (
                        <button
                            key={suggestion}
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                addTag(suggestion);
                            }}
                            className="w-full text-left px-3 py-1.5 text-xs text-stone-600 hover:bg-stone-50 transition"
                        >
                            {suggestion}
                        </button>
                    ))}
                    {isNewTag && (
                        <button
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                addTag(trimmedInput);
                            }}
                            className="w-full text-left px-3 py-1.5 text-xs text-stone-800 font-medium hover:bg-blue-50 transition flex items-center gap-1.5 border-t border-stone-100"
                        >
                            <Plus className="w-3.5 h-3.5 text-stone-500" />
                            Add "<span className="text-stone-900">{trimmedInput}</span>"
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default TagsInput;
